import { ReactNode } from 'react';
import { QuoteGetResponse } from '@lio/api-types';
import { IMenuItem } from '@components/menu';
import { UserRoles, allRoles, internalRoles } from '@models/user';
import { HeaderButtonProps } from '@components/header/HeaderButton';
import { BrokerageUserRole, LioUserRole } from '@lio/rule-engine';
import { Theme } from '@mui/system';
import { drawerListItems } from '@pages/PortalAdmin/PortalAdmin.constants';
import { getDrawerListItems } from '@pages/Settings/Settings.constants';
import { BrokerageDivisionType } from '@lio/rule-engine';
// 10/9/2024 need to investigate why mui material thme is unable to find the proper types.
// Right now, weneed to use mui system import to get proper the Theme type.

type NavLinkVisibilityArgs = { brokerageDivisionType?: BrokerageDivisionType };

export enum HeaderType {
  Nav = 'nav',
  NavTransparent = 'nav-transparent',
  NewQuote = 'new-quote',
  Detail = 'detail',
  Admin = 'admin',
  Payments = 'payments',
  MakeAPayment = 'make-a-payment',
  Settings = 'settings',
  PlaceHolder = 'place-holder',
}

export interface IHeaderAlertWrapper {
  title?: string;
  type: HeaderType;
  children?: ReactNode;
  quote?: QuoteGetResponse;
  actionItems?: IMenuItem[];
  navTitle?: string;
  showNavLinks?: boolean;
  buttons?: (HeaderButtonProps | React.ReactNode)[] | undefined;
  disableActionItems?: boolean;
  hasUnsavedChanges?: boolean;
  darkBackground?: boolean;
  smallLogo?: boolean;
}

// THEME-TODO: themize this with 'header' colors - this will most likely be a common area of color change for whitelisting

export const headerBgInitial = ({ darkBackground }: { darkBackground: boolean }) => ({
  [HeaderType.NavTransparent]: 'transparent',
  [HeaderType.Nav]: 'transparent',
  [HeaderType.Payments]: ({ palette }: Theme) => (darkBackground ? 'transparent' : palette.background.gradient),
  [HeaderType.MakeAPayment]: ({ palette }: Theme) => (darkBackground ? 'transparent' : palette.background.gradient),
  [HeaderType.Detail]: ({ palette }: Theme) => palette.background.main,
  [HeaderType.NewQuote]: 'transparent',
  [HeaderType.Admin]: ({ palette }: Theme) => palette.background.main,
  [HeaderType.Settings]: ({ palette }: Theme) => palette.background.main,
  [HeaderType.PlaceHolder]: ({ palette }: Theme) => palette.background.main,
});

export const headerBgScrolled: any = {
  [HeaderType.NavTransparent]: 'transparent',
  [HeaderType.Nav]: ({ palette }: Theme) => palette.background.gradient,
  [HeaderType.Payments]: ({ palette }: Theme) => palette.background.gradient,
  [HeaderType.MakeAPayment]: ({ palette }: Theme) => palette.background.gradient,
  [HeaderType.Detail]: ({ palette }: Theme) => palette.background.main,
  [HeaderType.NewQuote]: ({ palette }: Theme) => palette.background.gradient,
  [HeaderType.Settings]: ({ palette }: Theme) => palette.background.main,
};

export const getNavLinks = ({
  isTinyScreen,
  // isInternalUser,
  payerId,
  isCustomerUser,
  showSubjectivityLibrary = false,
}: {
  isTinyScreen: boolean;
  isInternalUser: boolean;
  payerId?: string;
  isCustomerUser?: boolean;
  showSubjectivityLibrary?: boolean;
}) => [
  {
    name: 'Home',
    url: '/',
    roles: allRoles,
  },
  {
    name: 'Quotes & Renewals',
    url: '/quotes',
    roles: Object.values({ ...LioUserRole, ...BrokerageUserRole }),
  },
  {
    name: 'Policies',
    url: '/policies',
    roles: Object.values({ ...LioUserRole, ...BrokerageUserRole }),
  },
  {
    name: 'Customers',
    url: '/customers',
    roles: internalRoles,
  },
  {
    name: 'Payment Center',
    url: '/payments',
    roles: allRoles,
    isVisible: ({ brokerageDivisionType }: NavLinkVisibilityArgs = {}) =>
      brokerageDivisionType !== BrokerageDivisionType.Wholesale,
  },
  // keep last
  {
    name: 'Portal Admin',
    url: '/portal-admin',
    matchPartialRoute: true,
    roles: [
      UserRoles.SystemAdmin,
      UserRoles.SrOps,
      UserRoles.JrOps,
      UserRoles.PortalAdmin,
      UserRoles.Underwriter,
      UserRoles.LIOContractor,
    ],
    mobileSubLinks: drawerListItems?.map((item) => ({
      name: item.name,
      url: `/portal-admin/${item.path}`,
      roles: item.roles,
    })),
  },

  // TODO: show on mobile only
  isTinyScreen
    ? {
        name: 'Account Settings',
        url: '/settings',
        roles: allRoles,
        mobileSubLinks: getDrawerListItems({
          hasPayerProfile: !!payerId,
          isNotCustomerUser: !isCustomerUser,
          showSubjectivityLibrary: showSubjectivityLibrary ?? false,
        })?.map((item) => ({
          name: item.name,
          url: item.path,
          roles: item.roles,
        })),
      }
    : null,
];

export const paymentLinks = [
  {
    name: 'Payment Center Home',
    url: '/payments',
    roles: allRoles,
  },
  {
    name: 'Account Management',
    url: '/payments/accounts',
    roles: allRoles,
  },
  {
    name: 'Payment History',
    url: '/payments/payment-history',
    roles: allRoles,
  },
  {
    name: 'Statement Credit',
    url: '/payments/statement-credit',
    roles: internalRoles,
  },
  {
    name: 'Auto Pay Enrollment',
    url: '/payments/auto-pay-enrollment',
    roles: [LioUserRole.SrOps, LioUserRole.SystemAdmin, LioUserRole.PortalAdmin],
  },
];

export const headerHeight = 80;
